export function numberToWords(number) {
    if (typeof number === "string") number = parseFloat(number.replace(/,/g, ""));
    if (isNaN(number)) return "Invalid input";

    // Round to two decimal places
    number = number.toFixed(2);

    const ones = [
        "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten",
        "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen",
        "eighteen", "nineteen",
    ];
    const tens = [
        "", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety",
    ];
    const scales = ["", "thousand", "million", "billion", "trillion"];

    if (parseFloat(number) === 0) return "Zero";

    const convertToWords = (num) => {
        if (num < 20) return ones[num];
        if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ` ${ones[num % 10]}` : "");
        if (num < 1000) {
            return (
                ones[Math.floor(num / 100)] +
                " hundred" +
                (num % 100 !== 0 ? ` ${convertToWords(num % 100)}` : "")
            );
        }
        return "";
    };

    const splitIntoGroups = (num) => {
        const groups = [];
        while (num > 0) {
            groups.push(num % 1000);
            num = Math.floor(num / 1000);
        }
        return groups;
    };

    const handleIntegerPart = (integerPart) => {
        const groups = splitIntoGroups(integerPart);
        const words = groups
            .map((group, index) => (group > 0 ? `${convertToWords(group)} ${scales[index]}` : ""))
            .filter(Boolean)
            .reverse()
            .join(" ");
        return words.trim();
    };

    const handleDecimalPart = (decimalPart) => {
        if (!decimalPart || decimalPart === "00") return "";
        return decimalPart
            .split("")
            .map(digit => ones[digit]) // No parseInt to avoid losing leading zeros
            .join(" ");
    };

    // Split the rounded number into integer and decimal parts
    const [integerPart, decimalPart] = number.split(".");

    let result = handleIntegerPart(parseInt(integerPart, 10));

    if (decimalPart !== "00") {
        result += ` and Fils ${handleDecimalPart(decimalPart)} only`;
    } else {
        result += " only";
    }

    // Capitalize the first letter of each word
    return result
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}



export const processInvoiceData = (data, invoiceNum,invoiceDetail) => {
    if (!Array.isArray(data) || data?.length === 0) {
      return null; // Handle empty or invalid data input
    }
  
    const invoiceData = data[0]; // First item contains general invoice details
    const placeholder = "N/A";
  
    // Extract unique invoices based on VBELN
    const uniqueInvoices = Array.from(new Map(data.map((item) => [item.VBELN, item])).values());
  
    // Extract unique invoice numbers from invoiceNum array
    const invoiceNumbers = Array.isArray(invoiceNum)
      ? [...new Set(invoiceNum.map((item) => item.VBELN))] // Ensure unique values
      : [];
  
    // Function to parse numbers safely
    const parseNumber = (value) => parseFloat(value?.toString().replace(/,/g, "")) || 0;
  
    // Calculate totals for ZPDISC and ZVAT
    const totals = data.reduce(
      (acc, item) => {
        acc.ZPDISC += parseNumber(item.ZPDISC || 0);
        acc.ZVAT += parseNumber(item.ZVAT || 0);
        acc.ZPVAT = parseNumber(item.ZPVAT || 0); // Assuming ZPVAT is a percentage value in data
        return acc;
      },
      {
        ZPDISC: 0,
        ZVAT: 0,
        ZPVAT: 0,
      }
    );
    
    // Calculate totals from uniqueInvoices
    totals.ZAVAT = uniqueInvoices.reduce((sum, item) => sum + parseNumber(item.ZAVAT || 0), 0);
    totals.ZADV = uniqueInvoices.reduce((sum, item) => sum + parseNumber(item.ZADV || 0), 0);
    totals.ZRET = uniqueInvoices.reduce((sum, item) => sum + parseNumber(item.ZRET || 0), 0);
    
    // Updated Calculations
    const ZPVATAmount = (totals.ZPVAT / 100) * totals.ZRET; // Calculate ZPVAT percentage of ZRET
    totals.ZVATafterZRET = totals.ZVAT - ZPVATAmount; // Adjusted calculation
    
    totals.ZBAMT = totals.ZPDISC - totals.ZADV;
    totals.ZBTAMT = totals.ZVATafterZRET - totals.ZAVAT;
    totals.ZFAMT = totals.ZBAMT - totals.ZRET + totals.ZBTAMT;
  
    // Structured data to send to backend
    return {
      invoiceName:invoiceDetail.invoiceName,
      description:invoiceDetail.description,
      invoiceNumber: invoiceNumbers?.length > 0 ? invoiceNumbers : [placeholder],
      invoiceDate: invoiceData.FKDAT || placeholder,
      salesRep: invoiceData.ENAME || placeholder,
      paymentTerms: invoiceData.TEXT1 || placeholder,
      poRefNo: invoiceData.BSTNK_VF || placeholder,
      deliveryNo: invoiceData.VGBEL || placeholder,

      billTo: {
        num:invoiceData.ZBP || placeholder,
        name: invoiceData.NAME1 || placeholder,
        address: invoiceData.LANDX50 || placeholder,
        city:invoiceData.CITY1 || placeholder,
        street: invoiceData.STREET || placeholder,
        postal:invoiceData.POST_CODE1 || placeholder,
        trn: invoiceData.ZBPTRN || placeholder,
      },
  
      shipTo: {
        num: invoiceData.ZSP || placeholder,
        name: invoiceData.ZNAME1 || placeholder,
        address: invoiceData.ZLANDX50 || placeholder,
        trn: invoiceData.ZSPTRN || placeholder,
        city:invoiceData.ZCITY1 || placeholder,
        street: invoiceData.ZSTREET || placeholder,
        postal:invoiceData.ZPOST_CODE1 || placeholder,
      },
  
      trigonTRN: invoiceData.ZCTRN || placeholder,
  
      items: data.map((item, index) => ({
        serialNo: index + 1,
        itemCode: item.MATNR || placeholder,
        description: item.ARKTX || placeholder,
        quantity: item.FKIMG || 0,
        rate: parseNumber(item.ZRATE || 0), 
        discount: parseNumber(item.ZDISC || 0),
        priceAfterDiscount: parseNumber(item.ZPDISC || 0),
        vatPercentage: parseNumber(item.ZPVAT || 0),
        vatAmount: parseNumber(item.ZVAT || 0),
        totalAmount: parseNumber(item.ZTAMT || 0),
      })),
  
      totals: {
        balanceAmountBeforeTax:totals.ZBAMT.toFixed(2),
        totalBeforeTax: totals.ZPDISC.toFixed(2),
        totalVAT: totals.ZVAT.toFixed(2),
        totalVatAfterRetention: totals.ZVATafterZRET.toFixed(2),
        advanceReceived: totals.ZADV.toFixed(2),
        finalAmount: totals.ZFAMT.toFixed(2),
        amountInWords: numberToWords(totals.ZFAMT),
        taxOnAdvance:totals.ZAVAT.toFixed(2),
        balanceTaxAmount:totals.ZBTAMT.toFixed(2),
        retentionAmount:totals.ZRET
      },
    };
  };
  